import {
  CEFR_LEVELS,
  EXAM_COOLDOWN_HOURS,
  EXAM_PASS_PCT,
  EXAM_SECTION_COUNTS,
  EXAM_SECTIONS,
  type CefrLevel,
} from "@rmnlng/shared";
import { ArrowLeft, GraduationCap, Play } from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useExamStart } from "@/lib/exam";
import { useT, type I18nKey } from "@/lib/i18n";

// Lobby — the user-facing "Start exam" screen for `/levels/:level/exam`.
// Renders the section breakdown + pass mark + cooldown rule, then a Start
// button that fires `useExamStart` and routes to the run screen on
// success. Errors (403 ready-gate, 429 cooldown, 400 not-enough-content)
// surface inline so the user knows what to do without leaving the page.
export function ExamLobbyPage() {
  const { level } = useParams<{ level: string }>();
  const navigate = useNavigate();
  const t = useT();

  const isValid = CEFR_LEVELS.includes(level as CefrLevel);
  const targetLevel = level as CefrLevel;
  const start = useExamStart(targetLevel);

  if (!isValid) return <Navigate to="/levels" replace />;

  async function onStart() {
    try {
      const res = await start.mutateAsync();
      // Hand the questions to the run screen via location state — they
      // came over the wire as part of the start response, no need to
      // refetch. attemptId travels with them.
      navigate(`/levels/${targetLevel}/exam/run`, {
        state: { attemptId: res.attemptId, startedAt: res.startedAt, questions: res.questions },
      });
    } catch {
      // Error rendered below from `start.error`. No-op here.
    }
  }

  // Error categorization — the server returns specific status codes for
  // each failure. Map them to UI-actionable copy. Anything else falls
  // through to a generic "could not start" line.
  const error = start.error as { status?: number; data?: { message?: string; retryAfter?: number } } | null;
  const errorBody = errorCopy(error, t);

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-4">
      {/* Back button + title row. No bottom nav on this route, so the
          back link is the only nav out (besides browser back). */}
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Link
          to={`/levels/${targetLevel}`}
          aria-label={t("common.back")}
          className="flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-[color:var(--surface-2)]"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-3">
            {t("exam.lobby_eyebrow", { level: targetLevel })}
          </div>
          <div className="text-[15px] font-semibold">{t("exam.lobby_title")}</div>
        </div>
      </div>

      {/* Hero card */}
      <div className="card-elevated rounded-[var(--r-lg)] p-5">
        <div className="mb-3 flex items-center gap-3">
          <div
            className="flex size-12 shrink-0 items-center justify-center rounded-[12px]"
            style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
          >
            <GraduationCap className="size-6" />
          </div>
          <div className="min-w-0">
            <div className="text-[18px] font-bold tracking-[-0.02em]">
              {t("exam.lobby_hero_title", { level: targetLevel })}
            </div>
            <div className="mt-0.5 text-[12px] text-text-3">
              {t("exam.lobby_hero_sub", {
                count: 50,
                pass: EXAM_PASS_PCT,
              })}
            </div>
          </div>
        </div>

        {/* Section breakdown — 5 rows, count + label per section. */}
        <ul className="mt-2 divide-y divide-border rounded-[var(--r-md)] border border-border bg-[color:var(--surface-2)]">
          {EXAM_SECTIONS.map((s) => (
            <li key={s} className="flex items-center justify-between px-3.5 py-2.5 text-[13px]">
              <span className="text-text-2">{t(sectionLabelKey(s))}</span>
              <span className="font-mono text-text-3 tabular-nums">{EXAM_SECTION_COUNTS[s]}</span>
            </li>
          ))}
        </ul>

        {/* Rules block — 3 short lines. Pass mark, cooldown, no-SRS-impact. */}
        <ul className="mt-4 space-y-1.5 text-[12px] text-text-2">
          <li>{t("exam.rule_pass_mark", { pass: EXAM_PASS_PCT })}</li>
          <li>{t("exam.rule_cooldown", { hours: EXAM_COOLDOWN_HOURS })}</li>
          <li>{t("exam.rule_no_srs")}</li>
        </ul>
      </div>

      {/* Error strip */}
      {errorBody && (
        <div
          className="rounded-[var(--r-md)] px-3.5 py-3 text-[12px]"
          style={{ background: "var(--again-soft)", color: "var(--again)" }}
        >
          {errorBody}
        </div>
      )}

      {/* Start button at the bottom — full-width, accent. */}
      <div className="mt-auto pt-2">
        <Button onClick={onStart} disabled={start.isPending} size="block">
          <Play className="size-4" />
          {start.isPending ? t("exam.starting") : t("exam.start_button", { level: targetLevel })}
        </Button>
      </div>
    </div>
  );
}

function sectionLabelKey(section: (typeof EXAM_SECTIONS)[number]): I18nKey {
  return `exam.section_${section}` as I18nKey;
}

function errorCopy(
  error: { status?: number; data?: { message?: string; retryAfter?: number } } | null,
  t: ReturnType<typeof useT>,
): string | null {
  if (!error) return null;
  if (error.status === 403) return t("exam.error_not_ready");
  if (error.status === 429) {
    const seconds = error.data?.retryAfter ?? 0;
    const hours = Math.ceil(seconds / 3600);
    return t("exam.error_cooldown", { hours });
  }
  if (error.status === 400) return error.data?.message ?? t("exam.error_generic");
  return t("exam.error_generic");
}
